import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  Container,
  Header,
  Icon,
  Pagination,
  Segment,
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";
import { getCallRecordings } from "../../api/transcriptions";
import { style } from "../../style";
import CallRecordingRow from "./CallRecordingRow";
import {
  RecordingsError,
  RecordingsLoading,
  RecordingsNotice,
  useRecordingsQuery,
} from "./CallRecordingsStatus";

const CallRecordings = ({
  setInfoModal,
  children,
  searchParams,
  setIsSearching,
  onViewProcessed,
}) => {
  const [sidekickHelpTextHidden, setSidekickHelpTextHidden] = useState(
    localStorage.getItem("sidekickHelpTextHidden") || false,
  );
  // A page belongs to the search it was picked in, so a new search is on
  // page 1 from its first render (no fetch for the old page number).
  const [pageState, setPageState] = useState({ page: 1, searchParams });
  const page = pageState.searchParams === searchParams ? pageState.page : 1;

  const queryClient = useQueryClient();

  const queryKey = ["call-recordings", page, searchParams];
  const status = useRecordingsQuery(
    queryKey,
    () => {
      return getCallRecordings({ ...searchParams, page });
    },
    {
      keepPreviousData: true,
      refetchIntervalInBackground: true,
      refetchInterval: (data) => {
        let interval = false;
        const recordings = data?.data?.data;
        if (recordings) {
          recordings.forEach((i) => {
            if (
              ["SUMMARISING", "TRANSCRIBING"].includes(
                i?.transcription?.status.value,
              )
            ) {
              queryClient.invalidateQueries({
                queryKey: ["call-transcriptions"],
              });
              interval = 30 * 1000;
            }
          });
        }
        return interval;
      },
    },
  );

  const recordingsQuery = status.query;

  useEffect(() => {
    setIsSearching(recordingsQuery.isRefetching);
  }, [setIsSearching, recordingsQuery.isRefetching]);

  const handlePaginationChange = (e, { activePage }) => {
    setPageState({ page: activePage, searchParams });
  };

  if (status.mode === "loading") {
    return (
      <Container style={{ marginTop: "20px" }}>
        <RecordingsLoading
          slow={status.slow}
          ipecsIssue={status.ipecsIssue}
          attempt={status.attempt}
          maxAttempts={status.maxAttempts}
          columns={8}
        />
      </Container>
    );
  }

  if (status.mode === "error") {
    return (
      <Container style={{ marginTop: "20px" }}>
        <RecordingsError
          error={recordingsQuery.error}
          retrying={status.retrying}
          retryIn={status.retryIn}
          onRetry={status.retry}
          onViewProcessed={onViewProcessed}
        />
      </Container>
    );
  }

  const meta = status.rows?.data?.meta;
  // Rows from another search don't match its pages, but a failed page change
  // within the same search can still page back.
  const sameSearch =
    !status.fallbackKey ||
    JSON.stringify(status.fallbackKey[2]) === JSON.stringify(searchParams);
  const paginationEnabled = meta?.last_page > 1 && sameSearch;
  const recordings = status.rows?.data?.data || [];

  return (
    <Container style={{ marginTop: "20px" }}>
      {!sidekickHelpTextHidden && (
        <Segment
          inverted
          style={{
            ...style.purpleGradientButton,
            marginBottom: "35px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "12px",
            }}
          >
            <Header size="medium" style={{ width: "80%", marginBottom: "0px" }}>
              Sidekick turbobcharges your workflow by summarising calls,
              generating meeting notes and helping you identify next actions for
              your projects.
            </Header>
            <Icon
              name="x"
              size="large"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setSidekickHelpTextHidden(true);
                localStorage.setItem("sidekickHelpTextHidden", true);
              }}
            />
          </div>
          <p>
            <i>
              If you choose "transcribe only" on a call recording below you can
              still use AI on this call at a later date.
            </i>
          </p>
        </Segment>
      )}
      {status.notice && (
        <RecordingsNotice
          kind={status.notice}
          ipecsIssue={status.ipecsIssue}
          updatedAt={status.updatedAt}
          attempt={status.attempt}
          maxAttempts={status.maxAttempts}
          retrying={status.retrying}
          retryIn={status.retryIn}
          onRetry={status.retry}
        />
      )}
      <Table basic="very" inverted>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Extension</TableHeaderCell>
            <TableHeaderCell>Number</TableHeaderCell>
            <TableHeaderCell>Direction</TableHeaderCell>
            <TableHeaderCell>Start Time</TableHeaderCell>
            <TableHeaderCell>End Time</TableHeaderCell>
            <TableHeaderCell>Timezone</TableHeaderCell>
            <TableHeaderCell>Duration</TableHeaderCell>
            <TableHeaderCell style={{ width: "140px" }}></TableHeaderCell>
          </TableRow>
        </TableHeader>

        <TableBody>
          {recordings.length === 0 && (
            <tr>
              <td colSpan={5} style={{ paddingTop: "12px" }}>
                No call recordings found.
              </td>
            </tr>
          )}

          {recordings.map((i) => {
            return (
              <CallRecordingRow
                id={i.slug}
                recordingRef={i.recording_ref}
                key={`recording-row-${i.slug}`}
                number={i.tel_number}
                direction={i.direction}
                start={i.start}
                end={i.end}
                extension={i.ext}
                duration={i.duration}
                timezone={i.effective_timezone}
                transcription={i.transcription}
                setInfoModal={setInfoModal}
                searchParams={searchParams}
                actionsDisabledReason={status.actionsDisabledReason}
              />
            );
          })}
        </TableBody>
      </Table>

      {meta && paginationEnabled ? (
        <div style={{ width: "100%", textAlign: "center", marginTop: "25px" }}>
          <Pagination
            inverted
            activePage={page}
            totalPages={meta.last_page}
            onPageChange={handlePaginationChange}
          />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "25px",
            padding: "10px 0",
          }}
        >
          <span style={{ color: "#fff" }}>
            Showing {recordings.length} result
            {recordings.length !== 1 ? "s" : ""}
          </span>
        </div>
      )}

      {children}
    </Container>
  );
};

export default CallRecordings;
